/**
 * Galaxy.tsx — M-PACT 3D Flowchart Wall
 *
 * W43 (2026-05-29) by Bryan K. Short.
 * W44.1 (2026-05-29): Full-bleed mode — OperatorShell removed on /galaxy routes.
 *   Platform chrome (topbar, sidebar, context tabs) is hidden on /galaxy and
 *   /galaxy/:accountId. All other routes are unaffected — the layout shell is
 *   only suppressed here. An "Exit Galaxy" ghost pill (top-left) lets users
 *   navigate back to /accounts or the prior route without getting trapped.
 *
 * Route: /galaxy          — no account context, all nodes unscored
 * Route: /galaxy/:accountId — highlights the path that account took
 */
import { Suspense, lazy, useEffect, useRef } from "react";
import { useParams, useLocation } from "wouter";

// Code-split the heavy 3D scene
const FlowchartWall = lazy(() => import("@/components/galaxy/FlowchartWall"));

// ─── Loading fallback ─────────────────────────────────────────────────────────
function GalaxyLoadingFallback() {
  return (
    <div
      style={{
        width: "100%",
        height: "100%",
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        background: "linear-gradient(135deg, #0a0a14 0%, #0d0d1a 40%, #1a1a2e 100%)",
        fontFamily: "Inter, system-ui, sans-serif",
        fontSize: 13,
        color: "rgba(148,163,184,0.6)",
        letterSpacing: "0.04em",
      }}
    >
      Loading flowchart wall…
    </div>
  );
}

// ─── Exit Galaxy pill ─────────────────────────────────────────────────────────
// Ghost-style pill, top-left. Navigates to prior route or /accounts as fallback.
function ExitGalaxyPill() {
  const [, setLoc] = useLocation();
  // Capture the referring location once on mount via document.referrer.
  // For SPA navigation, also try sessionStorage for a pre-galaxy path.
  const fallback = "/accounts";

  function handleExit(e: React.MouseEvent) {
    e.preventDefault();
    // Try to go back in SPA history; fall back to /accounts.
    try {
      const pre = sessionStorage.getItem("aip:preGalaxyPath");
      if (pre && pre !== "/galaxy" && !pre.startsWith("/galaxy/")) {
        setLoc(pre);
        return;
      }
    } catch { /* ignore */ }
    setLoc(fallback);
  }

  return (
    <button
      type="button"
      onClick={handleExit}
      aria-label="Exit Galaxy — return to accounts"
      style={{
        position: "fixed",
        top: 14,
        left: 14,
        zIndex: 200,
        display: "inline-flex",
        alignItems: "center",
        gap: 6,
        padding: "6px 13px",
        borderRadius: 20,
        border: "1px solid rgba(148,163,184,0.25)",
        background: "rgba(10,10,20,0.55)",
        backdropFilter: "blur(8px)",
        WebkitBackdropFilter: "blur(8px)",
        color: "rgba(148,163,184,0.75)",
        fontSize: 12,
        fontFamily: "Inter, system-ui, sans-serif",
        fontWeight: 500,
        letterSpacing: "0.03em",
        cursor: "pointer",
        transition: "border-color 0.15s, color 0.15s, background 0.15s",
        userSelect: "none",
        pointerEvents: "auto",
      }}
      onMouseEnter={(e) => {
        (e.currentTarget as HTMLButtonElement).style.borderColor = "rgba(15,118,110,0.6)";
        (e.currentTarget as HTMLButtonElement).style.color = "rgba(204,251,241,0.9)";
        (e.currentTarget as HTMLButtonElement).style.background = "rgba(10,10,20,0.80)";
      }}
      onMouseLeave={(e) => {
        (e.currentTarget as HTMLButtonElement).style.borderColor = "rgba(148,163,184,0.25)";
        (e.currentTarget as HTMLButtonElement).style.color = "rgba(148,163,184,0.75)";
        (e.currentTarget as HTMLButtonElement).style.background = "rgba(10,10,20,0.55)";
      }}
    >
      {/* Left-arrow SVG */}
      <svg width="12" height="12" viewBox="0 0 12 12" fill="none" aria-hidden="true">
        <path d="M7.5 2L3.5 6L7.5 10" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
      </svg>
      Exit Galaxy
    </button>
  );
}

// ─── Pre-galaxy path recorder ────────────────────────────────────────────────
// Writes the current path to sessionStorage before mounting the Galaxy page
// so ExitGalaxyPill can navigate back to the prior route.
function useRecordPreGalaxyPath() {
  const [loc] = useLocation();
  const recorded = useRef(false);
  useEffect(() => {
    if (recorded.current) return;
    recorded.current = true;
    try {
      // loc is the current hash path — we don't want to store /galaxy itself
      const prev = sessionStorage.getItem("aip:lastNonGalaxyPath");
      if (prev && !prev.startsWith("/galaxy")) {
        sessionStorage.setItem("aip:preGalaxyPath", prev);
      }
    } catch { /* ignore */ }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
}

// ─── Track last non-galaxy path ───────────────────────────────────────────────
// Put this hook at the App level if needed; here we record on every non-galaxy
// mount by checking if the stored path is not /galaxy already.
// Since Galaxy.tsx only mounts on /galaxy routes, the sessionStorage write
// in App.tsx via a route-change effect would be cleaner, but the self-contained
// approach below is sufficient for the exit-pill use case.

// ─── Main Galaxy page ─────────────────────────────────────────────────────────
export default function Galaxy() {
  const params = useParams<{ accountId?: string }>();
  const accountId = params?.accountId ?? null;

  useRecordPreGalaxyPath();

  useEffect(() => {
    document.title = "Galaxy · Mi6 AIP";
  }, []);

  return (
    // Full-bleed: 100vw × 100vh, no platform chrome.
    // position:fixed ensures we cover any scrolled content underneath.
    <div
      style={{
        position: "fixed",
        inset: 0,
        width: "100vw",
        height: "100vh",
        background: "#0a0a14",
        overflow: "hidden",
        zIndex: 10,
      }}
    >
      {/* Exit Galaxy pill — top-left ghost button */}
      <ExitGalaxyPill />

      {/* 3D flowchart wall fills full viewport */}
      <Suspense fallback={<GalaxyLoadingFallback />}>
        <FlowchartWall accountId={accountId} />
      </Suspense>
    </div>
  );
}
